// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export const GLOSSARY: Readonly<Record<string, { title: string; body: string }>> = {
  'changed-not-judged': {
    title: 'Changed, not judged',
    body: 'Edit distance and length tell you how much moved, never whether it moved for the worse. A diff of 400 characters is not worse than a diff of 3. Backslide shows them so you can sort your reading queue, and refuses to let them produce a verdict.',
  },
  alignment: {
    title: 'Trajectory alignment',
    body: 'Two agent runs are compared the way DNA sequences are, not position by position. An extra step at index 2 costs one gap instead of marking all thirteen remaining steps as different. The symbols read: = same step, ~ same tool with different arguments, X one step replaced by another, + a step inserted, - a step that vanished.',
  },
  fanout: {
    title: 'Branch',
    body: 'promptfoo runs every prompt against every provider, so one test case becomes several rows. Backslide compares each branch against its own counterpart — comparing one model’s answer to another model’s answer would report a regression on nearly every row.',
  },
  'join-provenance': {
    title: 'How the rows were matched',
    body: 'Backslide says how each pair was found: by the file’s own case id, by hashing the prompt text, or by similarity. It also says when it checked the ids and did not believe them — case ids in most eval formats are positions, so inserting a test case at the top shifts every one of them.',
  },
  ambiguous: {
    title: 'Ambiguous',
    body: 'Two rows in the same file with byte-identical inputs cannot be told apart by any method — including an embedding model, which would give them identical vectors. Backslide reports those as a group with a net change rather than pairing them, because the count would otherwise depend on which arbitrary pairing it happened to pick.',
  },
  precedence: {
    title: 'Counted once',
    body: 'An errored row has no output, so it would also fail a JSON check. If both counted, the columns would sum to more than the number of rows. The first check that explains a row owns it; everything below is marked not-counted.',
  },
  collision: {
    title: 'Collision',
    body: 'How many genuinely different sets of tool arguments have been folded into one symbol by the normalisation rules. One is correct. More than one means a rule is hiding a real difference — which is why the number is on screen rather than in a log.',
  },
};

/**
 * Click-to-define tooltips.
 *
 * Dismissal follows the same contract as every other overlay: Escape, a pointer anywhere outside,
 * and re-clicking the term itself. One event family only.
 */
export function initGlossary(tooltip: HTMLElement): void {
  let current: HTMLElement | null = null;

  const hide = (): void => {
    tooltip.setAttribute('hidden', '');
    current = null;
  };

  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.glossary-link[data-term]');
    if (!target) {
      if (current && !tooltip.contains(e.target as Node)) hide();
      return;
    }
    const term = target.dataset['term'];
    const entry = term ? GLOSSARY[term] : undefined;
    if (!entry) return;
    if (current === target) {
      hide();
      return;
    }
    e.preventDefault();
    tooltip.replaceChildren();
    const h = document.createElement('strong');
    h.textContent = entry.title;
    tooltip.append(h, document.createTextNode(entry.body));
    tooltip.removeAttribute('hidden');
    const r = target.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    tooltip.style.width = `${width}px`;
    tooltip.style.left = `${Math.max(12, Math.min(r.left, window.innerWidth - width - 12))}px`;
    const below = r.bottom + 8;
    tooltip.style.top =
      below + tooltip.offsetHeight > window.innerHeight
        ? `${Math.max(12, r.top - tooltip.offsetHeight - 8)}px`
        : `${below}px`;
    current = target;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) hide();
  });
  window.addEventListener('resize', hide);
}
